import React, { useState, useRef } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Modal, ModalBody, ModalHeader, Button } from "reactstrap";

// Utility function to center the crop
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const ImageCropper = ({ imageSrc, originalFile, onCropComplete, onClose }) => {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState(1);

  // Auto-center crop on image load
  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspect));
    setAspect(undefined);
  };

  // Generate cropped image from canvas
  const getCroppedImage = async () => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement("canvas");
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error("Failed to create blob");
          return;
        }

        const croppedFile = new File([blob], originalFile.name, {
          type: originalFile.type,
        });

        onCropComplete(croppedFile);
        onClose();
        resolve();
      }, originalFile.type);
    });
  };

  // Skip cropping and use original image
  const skipCrop = () => {
    onCropComplete(originalFile);
    onClose();
  };

  return (
    <Modal isOpen={!!imageSrc} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>Crop Image</ModalHeader>
      <ModalBody>
        <div className="d-flex justify-content-center">
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
            >
              <img
                ref={imgRef}
                alt="Crop preview"
                src={imageSrc}
                style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>

        <div className="d-flex justify-content-center mt-3 gap-2">
          {/* // Skip cropping and use original image */}
          <Button color="warning" onClick={skipCrop}>
            Skip Crop
          </Button>
          <Button
            color="success"
            onClick={getCroppedImage}
            disabled={!completedCrop}
          >
            Save
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ImageCropper;
